import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Formik
} from 'formik';
import * as yup from 'yup';
import NumberFormat from 'react-number-format';
import {
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  Button,
  Card,
  Typography
} from '@material-ui/core';
import {
  Breadcrumb,
  BackdropCircular,
  InfoTooltip
} from 'components';
import { requestTaxRatesConfigurationApi } from 'api';


const zipRegRxp = /(^\d{5}$)|(^\d{5}-\d{4}$)|\*/;

const TaxRatesOld = () => {
  const dispatch = useDispatch();
  const {profile: {stores = []}, loadingRequestTaxRAtesConfigurations} = useSelector((state) => state.user);
  const {storeCode = '', storeUrl = ''} = !!stores.length && stores[0];

  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <div className="mb-15">
        <Breadcrumb
          routeSegments={[
            { name: 'Settings', path: '/settings#go_live_requirements' },
            { name: 'Go Live Requirements', path: '/settings#go_live_requirements' },
            { name: 'Tax Rate' }
          ]}
        />
        <Formik
          initialValues={{
            taxRate: '',
            isZipRange: false,
            zipCode: '',
            zipRangeFrom: '',
            zipRangeTo: '',
            storeCode,
            storeUrl,
            message: ''
          }}
          onSubmit={(values, {resetForm}) => {
            requestTaxRatesConfigurationApi({dispatch, requestData: values});
            resetForm();
          }}
          enableReinitialize
          validationSchema={
            yup.object().shape({
              taxRate: yup
                .number()
                .nullable().typeError('Allowed numbers only')
                .required('Field is required'),
              isZipRange: yup
                .boolean(),
              zipRangeFrom: yup
                .string()
                .matches(zipRegRxp, 'Please enter a valid zip code (Ex: 90602 or 90602-1234)')
                .when('isZipRange', {
                  is: true,
                  then: yup.string().required('Field is required')
                }),
              zipRangeTo: yup
                .string()
                .matches(zipRegRxp, 'Please enter a valid zip code (Ex: 90602 or 90602-1234)')
                .when('isZipRange', {
                  is: true,
                  then: yup.string().required('Field is required')
                }),
              zipCode: yup
                .string()
                .matches(zipRegRxp, 'Please enter a valid zip code (Ex: 90602 or 90602-1234) or * to use taxes for all zip codes')
                .when('isZipRange', {
                  is: false,
                  then: yup.string().required('Field is required')
                }),
            }, [
              ['isZipRange', 'zipRangeFrom'],
              ['isZipRange', 'zipRangeTo'],
              ['isZipRange', 'zipCode']
            ])
          }
        >
          {({
            values,
            errors,
            handleChange,
            handleBlur,
            setFieldValue,
            handleSubmit
          }) => {
            return (
              <form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <Card elevation={0} className="p-8 my-10 mx-auto max-w-600">
                  <Typography className="font-semibold text-dark-gray mb-8" variant="body1">
                    Please fill in the following details for tax rate configurations.
                  </Typography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid container spacing={2} item xs={12}>
                      <Grid item xs={12}>
                        <NumberFormat
                          customInput={TextField}
                          suffix="%"
                          fullWidth
                          required
                          variant="outlined"
                          name="taxRate"
                          className="w-full mt-4"
                          label="Tax Rate"
                          onValueChange={(value) => {
                            setFieldValue('taxRate', value.floatValue);
                          }}
                          onBlur={handleBlur}
                          error={errors.taxRate && Boolean(errors.taxRate)}
                          helperText={errors.taxRate && errors.taxRate}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.isZipRange}
                              onChange={handleChange}
                              name="isZipRange"
                              color="primary"
                            />
                          }
                          label="Zip/Post is Range"
                        />
                      </Grid>
                      {!values.isZipRange ?
                        <Grid item xs={12} className="flex">
                          <TextField
                            fullWidth
                            required
                            variant="outlined"
                            name="zipCode"
                            className="w-full mt-4"
                            label="Zip Code"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.zipCode && Boolean(errors.zipCode)}
                            helperText={errors.zipCode && errors.zipCode}
                          />
                          <div className="mt-4">
                            <InfoTooltip title="Enter a valid zip code (Ex: 90602 or 90602-1234) or * - matches any zip code" />
                          </div>
                        </Grid>
                        :
                        <>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              required
                              variant="outlined"
                              name="zipRangeFrom"
                              className="w-full mt-4"
                              label="Zip Range From"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.zipRangeFrom && Boolean(errors.zipRangeFrom)}
                              helperText={errors.zipRangeFrom && errors.zipRangeFrom}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              required
                              variant="outlined"
                              name="zipRangeTo"
                              className="w-full mt-4"
                              label="Zip Range To"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.zipRangeTo && Boolean(errors.zipRangeTo)}
                              helperText={errors.zipRangeTo && errors.zipRangeTo}
                            />
                          </Grid>
                        </>
                      }
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows="3"
                          variant="outlined"
                          name="message"
                          className="w-full mt-4"
                          label="Message"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          inputProps={{ className: 'p-0' }}
                          error={errors.message && Boolean(errors.message)}
                          helperText={errors.message && errors.message}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
                <div className="mx-auto max-w-600">
                  <Button
                    color="primary"
                    variant="contained"
                    className="min-w-100"
                    type="submit"
                  >
                    Submit
                  </Button>
                </div>
              </form>
            );
          }}
        </Formik>
      </div>
      <BackdropCircular loading={loadingRequestTaxRAtesConfigurations} />
    </div>
  );
};

export default TaxRatesOld;
